/**
 * Whether the AI Casting Consultant (the async copilot: the brain on its host, the thread routes on the API) is open
 * for this person, read from the platform itself rather than a build flag. The Copilot page said "coming soon" to every
 * org, even where the brain answered, and the working conversation sat behind a build flag no image set (Arjun,
 * 2026-09-26). Now: the thread list answers (on); the routes are not mounted, so this deployment runs no brain (404,
 * off: lib/imports/copilot's copilotUnavailable, the same rule); the person's role may not use it (403, forbidden); a
 * read that failed otherwise (error). Pure.
 */
import { ApiClientError } from "@/lib/api-client";

export type CopilotAvailability = "loading" | "on" | "off" | "forbidden" | "error";

/** The consultant's page. */
export const CONSULTANT_PATH = "/app/twin/copilot";

/** Whether a path is the consultant's own page (a trailing slash does not matter). */
export function isConsultantPage(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === CONSULTANT_PATH;
}

/** The thread list's read, as the page holds it. */
export interface ThreadsReadState {
  /** Live API, a signed-in person and an active org: the read is made at all. */
  live: boolean;
  isSuccess: boolean;
  error: unknown;
}

export function copilotAvailabilityOf(s: ThreadsReadState): CopilotAvailability {
  if (!s.live) return "off";
  if (s.isSuccess) return "on";
  if (s.error instanceof ApiClientError) {
    if (s.error.status === 404) return "off";
    if (s.error.status === 403) return "forbidden";
  }
  if (s.error) return "error";
  return "loading";
}

/**
 * Whether the page shows the conversation: where the platform answers, and where a read failed, since the
 * conversation says why and offers Try again (a 5xx or a dropped connection is not a closed door).
 */
export function opensConsultant(a: CopilotAvailability): boolean {
  return a === "on" || a === "error";
}

/** What the top bar's consultant button does, and how it is named. */
export interface ConsultantLauncher {
  /** It opens the consultant's page; otherwise it toggles the coming-soon panel, as before. */
  opensPage: boolean;
  /** The small coming-soon mark on the button. */
  comingSoon: boolean;
  /** Its accessible name and tooltip. */
  label: string;
}

/**
 * The top bar's button. It opens the page wherever the page has something to say: the conversation, or, for a role
 * that may not use it, who to ask. The coming-soon panel stays only where no consultant runs, and while the platform
 * is asked (a first read, well under a second).
 */
export function consultantLauncher(a: CopilotAvailability): ConsultantLauncher {
  if (opensConsultant(a) || a === "forbidden") {
    return { opensPage: true, comingSoon: false, label: "AI Casting Consultant" };
  }
  return { opensPage: false, comingSoon: true, label: "AI Casting Consultant: coming soon" };
}

/** The sidebar item's pill: "Coming soon" exactly where the top bar keeps its coming-soon mark. */
export function consultantNavBadge(a: CopilotAvailability): string | undefined {
  return consultantLauncher(a).comingSoon ? "Coming soon" : undefined;
}

/** What the right-hand panel shows. */
export type PanelContent =
  /** The consultant's conversation, in the panel (next phase, step 3). */
  | "conversation"
  /** The person is on the consultant's page, which already shows the conversation. */
  | "here"
  /** A role that may not use the consultant: a note that points to its page, which says who to ask. */
  | "moved"
  /** No consultant runs (or the platform is still asked): the Phase-0 coming-soon chat, as before. */
  | "phase0";

/**
 * What the panel shows, and so where a page's "Ask" opens it: the conversation itself wherever the consultant is open
 * (opensConsultant), but not on the consultant's own page, which has the conversation already (a second, side by side,
 * would show the same thread twice).
 */
export function panelContent(a: CopilotAvailability, pathname: string): PanelContent {
  if (opensConsultant(a)) return isConsultantPage(pathname) ? "here" : "conversation";
  return consultantLauncher(a).opensPage ? "moved" : "phase0";
}

/**
 * Whether the tab at the screen's right edge shows (components/copilot/ConsultantEdgeTab): where the consultant answers
 * in the panel and the panel is closed. Not on the consultant's own page, which shows the conversation, and not where
 * no consultant runs, where the top bar keeps its old behaviour. Pure.
 */
export function edgeTabShows(content: PanelContent, isOpen: boolean): boolean {
  return content === "conversation" && !isOpen;
}
